// apps/admin/src/features/products/collection.api.ts
//
// dbg-20260803-025413 — admin Collection API wrappers.
//
// Mirrors category.api.ts endpoint-for-endpoint; the BE surface
// (collection.admin.routes.ts) is a one-for-one mirror of the Category one, so
// the Category & Collection page can drive both tabs through the same shapes.
//
// Deltas from Category: `label` + `labelColor` (the extra LABEL column), and
// slug is globally unique rather than sibling-scoped.

import { apiClient } from '@printway/shared/api';

interface SuccessEnvelope<T> {
  success: true;
  data: T;
  requestId: string;
}

interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string; details?: Record<string, unknown> };
  requestId: string;
}

interface LooseClient {
  GET: (
    path: string,
    init?: { headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  POST: (
    path: string,
    init?: { headers?: Record<string, string>; body?: unknown },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  PATCH: (
    path: string,
    init?: { headers?: Record<string, string>; body?: unknown },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  DELETE: (
    path: string,
    init?: { headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
}

function unwrap<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as SuccessEnvelope<T>).success === true &&
    'data' in payload
  ) {
    return (payload as SuccessEnvelope<T>).data;
  }
  return payload as T;
}

function envelopeError(payload: unknown): { code: string | null; message: string | null } {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    const err = (payload as ErrorEnvelope).error;
    return { code: err?.code ?? null, message: err?.message ?? null };
  }
  return { code: null, message: null };
}

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `idemp-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

export type CollectionStatus = 'Active' | 'Inactive';

export interface CollectionSEO {
  title: string;
  canonicalUrl?: string | null;
  googleIndex: boolean;
  metaKeywords?: string | null;
  metaDescription: string;
}

export interface CollectionRow {
  id: string;
  name: { en: string; vi?: string; fr?: string };
  slug: string;
  parentId: string | null;
  sortOrder: number;
  status: CollectionStatus;
  display?: boolean;
  alias?: string | null;
  /** Merchandising badge text — "Hot" / "New" / "Sale". */
  label?: string | null;
  /** Hex colour for the label text, e.g. "#E53935". */
  labelColor?: string | null;
  shortDescription?: string | null;
  blueprintIds: string[];
  image?: string | null;
  seo: CollectionSEO;
  createdAt?: number;
  updatedAt?: number;
  updatedBy?: string | null;
  updatedByEmail?: string | null;
  /** Direct children — the form uses it to lock the Parent picker. */
  childCount?: number;
}

export interface CreateCollectionInput {
  name: { en: string; vi?: string; fr?: string };
  parentId?: string | null;
  sortOrder?: number;
  status?: CollectionStatus;
  display?: boolean;
  alias?: string | null;
  label?: string | null;
  labelColor?: string | null;
  shortDescription?: string | null;
  blueprintIds?: string[];
  image?: string | null;
  seo?: Partial<CollectionSEO>;
}

export type UpdateCollectionInput = Partial<CreateCollectionInput>;

export interface ListCollectionsPagedFilters {
  status?: CollectionStatus;
  display?: boolean;
  search?: string;
  page: number;
  pageSize: number;
}

export interface ListCollectionsPagedResponse {
  items: CollectionRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ReorderCollectionsInput {
  items: Array<{ id: string; sortOrder: number }>;
}

export class CollectionApiError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'CollectionApiError';
    this.code = code;
    this.status = status;
  }
}

function throwApi(err: unknown, response: Response, fallback: string): never {
  const { code, message } = envelopeError(err);
  throw new CollectionApiError(code ?? 'NETWORK_ERROR', message ?? fallback, response.status);
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export async function listAdminCollectionsPaged(
  filters: ListCollectionsPagedFilters,
): Promise<ListCollectionsPagedResponse> {
  const c = apiClient() as unknown as LooseClient;
  const qs = new URLSearchParams();
  qs.set('page', String(filters.page));
  qs.set('pageSize', String(filters.pageSize));
  if (filters.status) qs.set('status', filters.status);
  if (filters.display !== undefined) qs.set('display', String(filters.display));
  const search = filters.search?.trim();
  if (search) qs.set('search', search);

  const { data, error, response } = await c.GET(`/api/v1/admin/collections?${qs.toString()}`);
  if (error || !response.ok) throwApi(error ?? data, response, 'Failed to load collections');
  return unwrap<ListCollectionsPagedResponse>(data);
}

/** Top-level rows only — parent picker on the Collection form. */
export async function listTopLevelCollections(): Promise<CollectionRow[]> {
  const c = apiClient() as unknown as LooseClient;
  const { data, error, response } = await c.GET(
    '/api/v1/admin/collections?topLevelOnly=true',
  );
  if (error || !response.ok) throwApi(error ?? data, response, 'Failed to load collections');
  return unwrap<CollectionRow[]>(data);
}

export async function getAdminCollection(id: string): Promise<CollectionRow> {
  const c = apiClient() as unknown as LooseClient;
  const { data, error, response } = await c.GET(
    `/api/v1/admin/collections/${encodeURIComponent(id)}`,
  );
  if (error || !response.ok) throwApi(error ?? data, response, 'Failed to load collection');
  return unwrap<CollectionRow>(data);
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export async function createAdminCollection(
  dto: CreateCollectionInput,
): Promise<CollectionRow> {
  const c = apiClient() as unknown as LooseClient;
  const { data, error, response } = await c.POST('/api/v1/admin/collections', {
    headers: { 'Idempotency-Key': newIdempotencyKey() },
    body: dto,
  });
  if (error || !response.ok) throwApi(error ?? data, response, 'Failed to create collection');
  return unwrap<CollectionRow>(data);
}

export async function updateAdminCollection(
  id: string,
  dto: UpdateCollectionInput,
): Promise<CollectionRow> {
  const c = apiClient() as unknown as LooseClient;
  const { data, error, response } = await c.PATCH(
    `/api/v1/admin/collections/${encodeURIComponent(id)}`,
    { headers: { 'Idempotency-Key': newIdempotencyKey() }, body: dto },
  );
  if (error || !response.ok) throwApi(error ?? data, response, 'Failed to update collection');
  return unwrap<CollectionRow>(data);
}

export async function reorderAdminCollections(
  input: ReorderCollectionsInput,
): Promise<{ reordered: number }> {
  const c = apiClient() as unknown as LooseClient;
  const { data, error, response } = await c.POST('/api/v1/admin/collections/reorder', {
    headers: { 'Idempotency-Key': newIdempotencyKey() },
    body: input,
  });
  if (error || !response.ok) throwApi(error ?? data, response, 'Failed to reorder collections');
  return unwrap<{ reordered: number }>(data);
}

export const collectionQueryKeys = {
  all: ['admin', 'collections'] as const,
  paged: (filters: ListCollectionsPagedFilters) =>
    ['admin', 'collections', 'paged', filters] as const,
  topLevel: () => ['admin', 'collections', 'top-level'] as const,
  detail: (id: string) => ['admin', 'collections', 'detail', id] as const,
};
